import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import NumberInput from "../../../components/DataEntry/NumberInput";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  Recipe,
  Product,
  Ingredient,
  RecipeIngredient,
  SemiFinishedProduct,
  RecipeLinePayload,
  RecipeParameterPayload,
  RecipeQuantityBasis,
  UnitType,
  ProductionUnit,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { ingredientTypeLabel } from "../../../utils/mesIngredientType";
import { calcRecipeMetrics, getValidationColor, getValidationStatus, formatPercentage } from "../../../utils/recipeMetrics";
import {
  isAbsoluteRecipe,
  parseScaleFactors,
  formatScaleFactor,
  formatQuantity,
} from "../../../utils/recipeQuantity";
import { RECIPE_PARAMETERS, emptyRecipeParameterValues } from "../../../constants/recipeParameters";

interface EditableIngredient {
  id?: number;
  line_type: "ingredient" | "semi_finished";
  ingredient_id?: number;
  child_recipe_id?: number;
  ingredient_name: string;
  ingredient_type: string;
  semi_finished_type?: string;
  /** Pekarska receptura (QuantityBasis 'baker_pct'). */
  baker_percentage: number;
  /** Slastičarska receptura (QuantityBasis 'absolute'): količina + jedinica. */
  quantity: number;
  unit_type_id?: number;
  sort_order: number;
  notes: string;
  isNew?: boolean;
  child_recipe?: Recipe;
}

/**
 * Recipes Page - Manage product recipes and ingredients
 * Now with full editing capabilities and baker percentage validation
 */
const Recipes = () => {
  savePathToLocalStorage();

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [semiFinished, setSemiFinished] = useState<SemiFinishedProduct[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Initialize the product filter from ?product_id= so other pages (e.g. Proizvodi)
  // can deep-link straight to a product's recipe.
  const initialProductId = (() => {
    const p = new URLSearchParams(window.location.search).get("product_id");
    return p ? Number(p) : null;
  })();
  const [filterProductId, setFilterProductId] = useState<number | null>(initialProductId);
  const [showHistory, setShowHistory] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingIngredients, setEditingIngredients] = useState<EditableIngredient[]>([]);
  const [originalRecipe, setOriginalRecipe] = useState<Recipe | null>(null);

  // Recipe parameters (mixer setpoints) are edited separately from the lines: they
  // are saved in place on this version, while a line edit publishes a new one.
  const [parameters, setParameters] = useState<Record<string, number | null>>(emptyRecipeParameterValues());
  const [originalParameters, setOriginalParameters] = useState<Record<string, number | null>>(emptyRecipeParameterValues());
  const [hasSavedParameters, setHasSavedParameters] = useState(false);
  const [isEditingParameters, setIsEditingParameters] = useState(false);
  const [savingParameters, setSavingParameters] = useState(false);

  // Slastičarska receptura: jedinice po liniji i množitelj kojim se gleda.
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [scaleFactor, setScaleFactor] = useState(1);
  const [productionUnits, setProductionUnits] = useState<ProductionUnit[]>([]);
  // Pogon se bira ovdje jer se receptura tu i uređuje; osnova količine ga prati,
  // pa korisnik nema dvije postavke koje moraju pogoditi jedna drugu.
  const [unitDraft, setUnitDraft] = useState<number | null>(null);
  const [scaleFactorsDraft, setScaleFactorsDraft] = useState("");

  const basisDraft: RecipeQuantityBasis =
    productionUnits.find((u) => u.ID === unitDraft)?.Code === "slasticarna" ? "absolute" : "baker_pct";

  // Dok se uređuje, tablica prati odabrani pogon; spremljena receptura se čita
  // po vlastitoj osnovi, da arhivska verzija ostane onakva kakva je spremljena.
  const isAbsolute = isEditing ? basisDraft === "absolute" : isAbsoluteRecipe(selectedRecipe);
  const scaleFactors = parseScaleFactors(isEditing ? scaleFactorsDraft : selectedRecipe?.ScaleFactors);
  // Grami su uvjerljivo najčešći, pa nova linija kreće od njih.
  const defaultUnitTypeId = unitTypes.find((u) => u.Code === "g")?.ID;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadRecipes();
  }, [filterProductId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsData, ingredientsData, semiFinishedData, unitTypesData, productionUnitsData] = await Promise.all([
        mesApi.getProducts(),
        mesApi.getIngredients({ is_active: true }),
        mesApi.getSemiFinishedProducts({ is_active: true }),
        mesApi.getUnitTypes(),
        mesApi.getProductionUnits(),
      ]);
      setProducts(productsData);
      setIngredients(ingredientsData);
      setSemiFinished(semiFinishedData);
      setUnitTypes(unitTypesData);
      setProductionUnits(productionUnitsData);
      await loadRecipes();
    } catch (err: any) {
      toast.error("Greska pri ucitavanju: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      const params: { product_id?: number } = {};
      if (filterProductId) {
        params.product_id = filterProductId;
      }
      const data = await mesApi.getRecipes(params);
      setRecipes(data);
    } catch (err: any) {
      toast.error("Greska pri ucitavanju recepata: " + err.message);
    }
  };

  const getProductId = (p: Product) => p.ID;
  const getRecipeId = (r: Recipe) => r.ID;
  const getIngredientId = (i: Ingredient) => i.ID;

  const getProductName = (productId: number) => {
    return products.find((p) => getProductId(p) === productId)?.ProductName || "N/A";
  };

  const getIngredientName = (ingredientId: number) => {
    return ingredients.find((i) => getIngredientId(i) === ingredientId)?.IngredientName || "N/A";
  };

  const getIngredientType = (ingredientId: number) => {
    const ing = ingredients.find((i) => getIngredientId(i) === ingredientId);
    return ingredientTypeLabel(ing?.IngredientTypeName) || "";
  };

  const getRecipeName = (recipe: Recipe) => recipe.RecipeCode || `Recipe ${getRecipeId(recipe)}`;
  const getRecipeVersion = (recipe: Recipe) => recipe.RecipeVersion || 1;

  // Every saved version is a separate Recipe row, so the API returns all of them.
  // Rendering them as a flat list ordered by recipe ID meant a freshly saved version
  // jumped to the top of the table while the product's earlier rows stayed put and
  // flipped to "Neaktivan" — which reads as "the product went inactive and the old
  // recipe is back". Group per product instead: current version first, older ones
  // only on request.
  const productGroups = useMemo(() => {
    const byProduct = new Map<number, Recipe[]>();
    recipes.forEach((recipe) => {
      const productId = recipe.Product ?? 0;
      const group = byProduct.get(productId);
      if (group) group.push(recipe);
      else byProduct.set(productId, [recipe]);
    });

    return Array.from(byProduct.entries())
      .map(([productId, versions]) => {
        const sorted = [...versions].sort((a, b) => getRecipeVersion(b) - getRecipeVersion(a));
        const current = sorted.find((r) => r.IsActive) || sorted[0];
        return {
          productId,
          current,
          older: sorted.filter((r) => r !== current),
        };
      })
      .sort((a, b) => getProductName(a.productId).localeCompare(getProductName(b.productId), "hr"));
  }, [recipes, products]);

  const recipeLineGroup = (notes?: string | null) => {
    const n = (notes || "").toLowerCase();
    if (n.includes("1. tijesto") || n.includes("1.tijesto")) return "1. tijesto";
    if (n.includes("2. tijesto") || n.includes("2.tijesto")) return "2. tijesto";
    if (n.includes("preljev")) return "Preljev";
    if (n.includes("posip")) return "Posip";
    return null;
  };

  const groupedViewLines = (lines: EditableIngredient[]) => {
    const groups: { title: string | null; lines: { ing: EditableIngredient; idx: number }[] }[] = [];
    lines.forEach((ing, idx) => {
      const title = recipeLineGroup(ing.notes);
      const last = groups[groups.length - 1];
      if (!last || last.title !== title) groups.push({ title, lines: [{ ing, idx }] });
      else last.lines.push({ ing, idx });
    });
    return groups;
  };

  const getTotalBakerPercentage = (ings: EditableIngredient[] | RecipeIngredient[]): number => {
    const total = (ings as { baker_percentage?: number; BakerPercentage?: number }[]).reduce(
      (sum, ing) => sum + (Number(ing.baker_percentage ?? ing.BakerPercentage) || 0),
      0
    );
    return isNaN(total) ? 0 : total;
  };

  const renderBasisBadge = (recipe: Recipe) =>
    isAbsoluteRecipe(recipe) ? (
      <span className="unit-badge unit-slasticarna">Kolicine</span>
    ) : (
      <span className="unit-badge unit-pekara">PEK %</span>
    );

  const toMetricsInput = (ings: EditableIngredient[]): RecipeIngredient[] =>
    ings.map((ing) => ({
      ID: ing.id || 0,
      Recipe: getRecipeId(selectedRecipe!),
      Ingredient: ing.line_type === "ingredient" ? ing.ingredient_id : null,
      ChildRecipe: ing.line_type === "semi_finished" ? ing.child_recipe_id : null,
      BakerPercentage: ing.baker_percentage,
      Ingredient_IngredientToRecipeIngredient:
        ing.line_type === "ingredient"
          ? ({ IngredientTypeName: ing.ingredient_type, IngredientName: ing.ingredient_name } as Ingredient)
          : undefined,
      Recipe_RecipeToRecipeIngredient_ChildRecipe: ing.child_recipe,
    }));

  const getFlourPercentage = (ings: EditableIngredient[]) =>
    calcRecipeMetrics(toMetricsInput(ings)).flour_total;

  const getHydrationPhase1 = (ings: EditableIngredient[]) => {
    const m = calcRecipeMetrics(toMetricsInput(ings));
    return m.flour_total > 0 ? m.hydration_phase_1.toFixed(1) : "0.0";
  };

  const getTotalHydration = (ings: EditableIngredient[]) => {
    const m = calcRecipeMetrics(toMetricsInput(ings));
    return m.flour_total > 0 ? m.total_hydration.toFixed(1) : "0.0";
  };

  const isFlourValid = (ings: EditableIngredient[]) => {
    const flourPct = getFlourPercentage(ings);
    return Math.abs(flourPct - 100) <= 0.5;
  };

  const mapRecipeLineToEditable = (ri: RecipeIngredient): EditableIngredient => {
    if (ri.ChildRecipe && ri.Recipe_RecipeToRecipeIngredient_ChildRecipe) {
      const sf = ri.Recipe_RecipeToRecipeIngredient_ChildRecipe.SemiFinishedProduct;
      return {
        id: ri.ID,
        line_type: "semi_finished",
        child_recipe_id: ri.ChildRecipe,
        ingredient_name: sf?.Name || `Poluproizvod #${ri.ChildRecipe}`,
        ingredient_type: sf?.SemiFinishedType || "semi_finished",
        semi_finished_type: sf?.SemiFinishedType,
        baker_percentage: Number(ri.BakerPercentage) || 0,
        quantity: Number(ri.Quantity) || 0,
        unit_type_id: ri.UnitType ?? undefined,
        sort_order: ri.SortOrder || 0,
        notes: ri.Notes || "",
        child_recipe: ri.Recipe_RecipeToRecipeIngredient_ChildRecipe,
      };
    }
    return {
      id: ri.ID,
      line_type: "ingredient",
      ingredient_id: ri.Ingredient || undefined,
      ingredient_name: ri.Ingredient_IngredientToRecipeIngredient?.IngredientName || getIngredientName(ri.Ingredient!),
      ingredient_type: ri.Ingredient_IngredientToRecipeIngredient?.IngredientTypeName || getIngredientType(ri.Ingredient!),
      baker_percentage: Number(ri.BakerPercentage) || 0,
      quantity: Number(ri.Quantity) || 0,
      unit_type_id: ri.UnitType ?? undefined,
      sort_order: ri.SortOrder || 0,
      notes: ri.Notes || "",
    };
  };

  const getSemiFinishedRecipeId = (sf: SemiFinishedProduct) =>
    sf.recipe?.ID;

  /**
   * The recipe detail only carries the parameters that were actually saved; the
   * rest stay empty. Every product is mixed differently, so an unset setpoint must
   * read as blank rather than as a number somebody chose for this recipe.
   */
  const parametersFromRecipe = (recipe: Recipe): Record<string, number | null> => {
    const values = emptyRecipeParameterValues();
    (recipe.RecipeParameter || []).forEach((param) => {
      if (param.ParamKey in values && param.ParamValue !== null && param.ParamValue !== undefined) {
        values[param.ParamKey] = Number(param.ParamValue);
      }
    });
    return values;
  };

  /** Populate the modal from a recipe that already carries its full line tree. */
  const showRecipeDetail = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setOriginalRecipe(recipe);
    setIsEditing(false);
    setIsEditingParameters(false);

    // Convert recipe ingredients to editable format
    const editable = (recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [])
      .map(mapRecipeLineToEditable)
      .sort((a, b) => a.sort_order - b.sort_order);
    setEditingIngredients(editable);

    const params = parametersFromRecipe(recipe);
    setParameters(params);
    setOriginalParameters(params);
    setHasSavedParameters((recipe.RecipeParameter || []).length > 0);
    setUnitDraft(products.find((p) => getProductId(p) === recipe.Product)?.ProductionUnit ?? null);
    setScaleFactorsDraft(recipe.ScaleFactors ?? "");
    setScaleFactor(1);
    setExpandedRows({});
  };

  /**
   * The list only carries line ids and baker percentages, so opening a recipe
   * fetches that one recipe: the ingredient names, the semi-finished sub-tree and
   * the flour/hydration metrics all read fields the list leaves out. The header
   * from the list row is shown straight away so the modal doesn't wait on the
   * network to appear.
   */
  const handleOpenRecipe = async (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setOriginalRecipe(null);
    setIsEditing(false);
    setIsEditingParameters(false);
    setEditingIngredients([]);
    setExpandedRows({});
    setDetailLoading(true);
    try {
      showRecipeDetail(await mesApi.getRecipe(getRecipeId(recipe)));
    } catch (err: any) {
      toast.error("Greska pri ucitavanju recepta: " + err.message);
      handleCloseModal();
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedRecipe(null);
    setOriginalRecipe(null);
    setIsEditing(false);
    setIsEditingParameters(false);
    setEditingIngredients([]);
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleStartEditParameters = () => {
    setIsEditingParameters(true);
  };

  const handleCancelEditParameters = () => {
    setParameters(originalParameters);
    setIsEditingParameters(false);
  };

  const handleUpdateParameter = (key: string, value: number) => {
    // NumberInput reports its emptyValue (NaN here) while the field is blank; that
    // is stored as null so an empty field stays unset instead of becoming 0.
    setParameters((prev) => ({ ...prev, [key]: Number.isFinite(value) ? value : null }));
  };

  /**
   * Saves onto the current version rather than publishing a new one: setpoints get
   * tuned between batches, and a version per nudge would bury the ingredient
   * history. Blank fields are sent as null and stored as absent, so clearing a
   * parameter removes it rather than writing a zero.
   */
  const handleSaveParameters = async () => {
    if (!selectedRecipe || savingParameters) return;

    setSavingParameters(true);
    try {
      const payload: RecipeParameterPayload[] = RECIPE_PARAMETERS.map((def) => ({
        ParamKey: def.key,
        ParamValue: parameters[def.key] ?? null,
      }));
      const saved = await mesApi.updateRecipeParameters(getRecipeId(selectedRecipe), payload);

      const values = emptyRecipeParameterValues();
      saved.forEach((param) => {
        if (param.ParamKey in values) {
          values[param.ParamKey] = param.ParamValue === null ? null : Number(param.ParamValue);
        }
      });
      setParameters(values);
      setOriginalParameters(values);
      // Mirror the recipe-detail payload, which carries only the rows that exist —
      // keeping the unset ones here would make them reload as zeros.
      const storedRows = saved.filter((param) => param.ParamValue !== null);
      setHasSavedParameters(storedRows.length > 0);
      setSelectedRecipe({ ...selectedRecipe, RecipeParameter: storedRows });
      setIsEditingParameters(false);
      toast.success("Recepturni parametri spremljeni");
    } catch (err: any) {
      toast.error("Greska pri spremanju parametara: " + err.message);
    } finally {
      setSavingParameters(false);
    }
  };

  const handleCancelEdit = () => {
    // Restore original ingredients
    if (originalRecipe) {
      const editable = (originalRecipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [])
        .map(mapRecipeLineToEditable)
        .sort((a, b) => a.sort_order - b.sort_order);
      setEditingIngredients(editable);
    }
    setIsEditing(false);
  };

  const nextSortOrder = () =>
    editingIngredients.length > 0
      ? Math.max(...editingIngredients.map((i) => i.sort_order)) + 1
      : 1;

  const handleAddRawIngredient = () => {
    setEditingIngredients([
      ...editingIngredients,
      {
        line_type: "ingredient",
        ingredient_id: 0,
        ingredient_name: "",
        ingredient_type: "",
        baker_percentage: 0,
        quantity: 0,
        unit_type_id: defaultUnitTypeId,
        sort_order: nextSortOrder(),
        notes: "",
        isNew: true,
      },
    ]);
  };

  const handleAddSemiFinished = () => {
    setEditingIngredients([
      ...editingIngredients,
      {
        line_type: "semi_finished",
        child_recipe_id: 0,
        ingredient_name: "",
        ingredient_type: "semi_finished",
        baker_percentage: 0,
        quantity: 0,
        unit_type_id: defaultUnitTypeId,
        sort_order: nextSortOrder(),
        notes: "",
        isNew: true,
      },
    ]);
  };

  const handleRemoveIngredient = (index: number) => {
    setEditingIngredients(editingIngredients.filter((_, i) => i !== index));
  };

  const handleUpdateIngredient = (index: number, field: keyof EditableIngredient, value: any) => {
    const updated = [...editingIngredients];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-fill name and type when selecting ingredient
    if (field === "ingredient_id" && value) {
      const ing = ingredients.find((i) => getIngredientId(i) === Number(value));
      if (ing) {
        updated[index].ingredient_name = ing.IngredientName;
        updated[index].ingredient_type = ing.IngredientTypeName || "";
        updated[index].line_type = "ingredient";
      }
    }
    if (field === "child_recipe_id" && value) {
      const sf = semiFinished.find((s) => getSemiFinishedRecipeId(s) === Number(value));
      if (sf) {
        updated[index].child_recipe_id = getSemiFinishedRecipeId(sf);
        updated[index].ingredient_name = sf.Name;
        updated[index].ingredient_type = sf.SemiFinishedType;
        updated[index].semi_finished_type = sf.SemiFinishedType;
        updated[index].line_type = "semi_finished";
        updated[index].child_recipe = sf.recipe || undefined;
      }
    }

    setEditingIngredients(updated);
  };

  const handleMoveIngredient = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === editingIngredients.length - 1) return;

    const updated = [...editingIngredients];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    // Swap sort orders
    const tempOrder = updated[index].sort_order;
    updated[index].sort_order = updated[swapIndex].sort_order;
    updated[swapIndex].sort_order = tempOrder;

    // Swap positions
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];

    setEditingIngredients(updated);
  };

  const handleSaveRecipe = async () => {
    // Every save persists a NEW recipe version, so a second click while the
    // first request is still running would create a duplicate version.
    if (!selectedRecipe || saving) return;

    const product = products.find((p) => getProductId(p) === selectedRecipe.Product);
    const unitChanged = (unitDraft ?? null) !== (product?.ProductionUnit ?? null);
    const basisChanged = basisDraft !== (selectedRecipe.QuantityBasis ?? "baker_pct");
    const absolute = basisDraft === "absolute";

    // Pekarsko pravilo o brašnu vrijedi samo kad su linije postoci; slastičarska
    // receptura nema brašno kao bazu pa se provjerava da svaka linija ima količinu.
    if (absolute) {
      const missing = editingIngredients.filter((i) => !(i.quantity > 0) || !i.unit_type_id);
      if (missing.length > 0) {
        toast.error("Sve linije moraju imati količinu veću od nule i jedinicu");
        return;
      }
    } else if (!isFlourValid(editingIngredients)) {
      toast.error("Postotak brasna mora biti 100% (±0.5%)");
      return;
    }

    // Validate all ingredients have valid IDs
    const invalidIngredients = editingIngredients.filter(
      (i) =>
        (i.line_type === "ingredient" && !i.ingredient_id) ||
        (i.line_type === "semi_finished" && !i.child_recipe_id)
    );
    if (invalidIngredients.length > 0) {
      toast.error("Sve linije moraju biti ispravno odabrane");
      return;
    }

    setSaving(true);
    try {
      // Pogon i osnova idu prvi: nova verzija recepture nasljeđuje osnovu s
      // trenutne, pa linije moraju biti spremljene u osnovi koja je do tada već
      // upisana. Promjena pogona na backendu sama povlači osnovu.
      if (unitChanged) {
        await mesApi.updateProduct(selectedRecipe.Product, { ProductionUnit: unitDraft });
      }
      if (basisChanged || (scaleFactorsDraft ?? "") !== (selectedRecipe.ScaleFactors ?? "")) {
        await mesApi.setRecipeQuantityBasis(getRecipeId(selectedRecipe), {
          QuantityBasis: basisDraft,
          ScaleFactors: scaleFactorsDraft || null,
        });
      }

      // Šalje se samo količina koja u ovoj osnovi ima značenje — backend drugu
      // sprema kao NULL, da linija ne tvrdi i postotak i grame.
      const quantityFields = (ing: EditableIngredient) =>
        absolute
          ? { Quantity: ing.quantity, UnitType: ing.unit_type_id ?? null }
          : { BakerPercentage: ing.baker_percentage };

      const ingredientsPayload: RecipeLinePayload[] = editingIngredients.map((ing, idx) =>
        ing.line_type === "semi_finished"
          ? {
              LineType: "semi_finished",
              ChildRecipe: ing.child_recipe_id!,
              ...quantityFields(ing),
              SortOrder: idx + 1,
              Notes: ing.notes || null,
            }
          : {
              LineType: "ingredient",
              Ingredient: ing.ingredient_id!,
              ...quantityFields(ing),
              SortOrder: idx + 1,
              Notes: ing.notes || null,
            }
      );

      const saved = await mesApi.updateRecipeIngredients(getRecipeId(selectedRecipe), ingredientsPayload);
      await Promise.all([
        loadRecipes(),
        unitChanged ? mesApi.getProducts().then(setProducts) : Promise.resolve(),
      ]);
      // Re-open the version the server returned rather than closing the modal: the user
      // stays on the recipe they just saved and sees the persisted lines and version
      // number, instead of being dropped back into the list next to the archived rows.
      if (saved) {
        // The save response is already a full recipe, so show it directly rather
        // than round-tripping through handleOpenRecipe to fetch what we just got.
        showRecipeDetail(saved);
        toast.success(`Recept spremljen kao nova verzija v${getRecipeVersion(saved)}`);
      } else {
        handleCloseModal();
        toast.success("Recept spremljen kao nova verzija");
      }
    } catch (err: any) {
      toast.error("Greska pri spremanju: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Slastičarska receptura: množitelj kojim se gleda i zbroj po jedinici.
   * Pekarskih metrika ovdje nema — hidracija i "Brašno PEK% = 100%" računaju se
   * u odnosu na brašno, kojeg ova receptura nema kao bazu.
   */
  const renderAbsoluteAnalytics = () => {
    const factors = [1, ...scaleFactors.filter((f) => f !== 1)];
    const totals = new Map<string, number>();
    for (const ing of editingIngredients) {
      const qty = (Number(ing.quantity) || 0) * scaleFactor;
      if (!qty) continue;
      const unit = unitTypes.find((u) => u.ID === ing.unit_type_id)?.Code || "—";
      totals.set(unit, (totals.get(unit) ?? 0) + qty);
    }

    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: "0.75rem" }}>
          <span className="detail-label" style={{ marginRight: 4 }}>Množitelj</span>
          {factors.map((f) => (
            <button
              key={f}
              type="button"
              className={f === scaleFactor ? "btn-submit btn-sm" : "btn-secondary btn-sm"}
              onClick={() => setScaleFactor(f)}
            >
              {f === 1 ? "Osnovna" : formatScaleFactor(f)}
            </button>
          ))}
          {scaleFactors.length === 0 && (
            <span className="modal-hint" style={{ margin: 0 }}>
              Množitelji se postavljaju na recepturi (npr. 5, 7.5, 10).
            </span>
          )}
        </div>
        <div
          className="recipe-metrics"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "1rem" }}
        >
          <div className="mes-metric-card">
            <div className="metric-label">Broj linija</div>
            <div className="metric-value">{editingIngredients.length}</div>
          </div>
          {Array.from(totals.entries())
            .sort((a, b) => a[0].localeCompare(b[0], "hr"))
            .map(([unit, total]) => (
              <div key={unit} className="mes-metric-card">
                <div className="metric-label">Ukupno ({unit})</div>
                <div className="metric-value">{formatQuantity(total)}</div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderRecipeAnalytics = () => {
    if (isAbsolute) return renderAbsoluteAnalytics();

    const metrics = calcRecipeMetrics(toMetricsInput(editingIngredients));
    const validationColor = getValidationColor(metrics);
    const validationStatus = getValidationStatus(metrics);

    return (
      <>
        {(metrics.errors.length > 0 || metrics.warnings.length > 0) && (
          <div
            style={{
              padding: "15px",
              backgroundColor: validationColor + "22",
              border: `2px solid ${validationColor}`,
              borderRadius: "8px",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "18px", marginRight: "10px" }}>
                {metrics.errors.length > 0 ? "❌" : "⚠️"}
              </span>
              <span style={{ fontWeight: "bold", color: validationColor }}>
                {validationStatus}: Recipe Validation
              </span>
            </div>
            {metrics.errors.map((error, i) => (
              <div key={`err-${i}`} style={{ color: "#e74c3c", fontSize: "14px", marginLeft: "28px" }}>
                • {error}
              </div>
            ))}
            {metrics.warnings.map((warning, i) => (
              <div key={`warn-${i}`} style={{ color: "#f39c12", fontSize: "14px", marginLeft: "28px" }}>
                • {warning}
              </div>
            ))}
          </div>
        )}

        <div
          className="recipe-metrics"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div className="mes-metric-card">
            <div className="metric-label">Ukupno PEK</div>
            <div className="metric-value">{getTotalBakerPercentage(editingIngredients).toFixed(1)}%</div>
          </div>
          <div className={`mes-metric-card ${isFlourValid(editingIngredients) ? "" : "invalid"}`}>
            <div className="metric-label">Brasno PEK</div>
            <div
              className="metric-value"
              style={{ color: isFlourValid(editingIngredients) ? "#4b9987" : "#e74c3c" }}
            >
              {getFlourPercentage(editingIngredients).toFixed(1)}%
            </div>
            {!isFlourValid(editingIngredients) && (
              <div className="metric-delta" style={{ color: "#e74c3c" }}>
                Mora biti 100%
              </div>
            )}
          </div>
          <div className="mes-metric-card">
            <div className="metric-label">Hidracija faza 1</div>
            <div className="metric-value">{getHydrationPhase1(editingIngredients)}%</div>
          </div>
          <div className="mes-metric-card">
            <div className="metric-label">Ukupna hidracija</div>
            <div className="metric-value">{getTotalHydration(editingIngredients)}%</div>
          </div>
        </div>
      </>
    );
  };

  const renderViewMode = () => (
    <>
      <div className="recipe-details-grid">
        <div className="detail-item">
          <span className="detail-label">Proizvod</span>
          <span className="detail-value">{getProductName(selectedRecipe!.Product ?? 0)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Verzija</span>
          <span className="detail-value">v{getRecipeVersion(selectedRecipe!)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Pogon</span>
          <span className="detail-value">
            {productionUnits.find(
              (u) => u.ID === products.find((p) => getProductId(p) === selectedRecipe!.Product)?.ProductionUnit,
            )?.Name ?? "—"}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Status verzije</span>
          <span className={`status-pill status-${selectedRecipe!.IsActive ? "active" : "inactive"}`}>
            <span className="status-dot"></span>
            {selectedRecipe!.IsActive ? "Aktivna verzija" : "Arhivirana verzija"}
          </span>
        </div>
      </div>

      {!detailLoading && renderRecipeAnalytics()}

      {selectedRecipe?.Notes && (
        <div className="recipe-notes">
          <strong>Biljeske:</strong> {selectedRecipe.Notes}
        </div>
      )}

      <h4 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>Sastojci</h4>
      {detailLoading ? (
        <div className="empty-state" style={{ padding: "1rem" }}>
          <p>Ucitavanje sastojaka...</p>
        </div>
      ) : editingIngredients.length > 0 ? (
        <div className="mes-table-wrap">
          <table className="mes-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Sastojak</th>
              <th>Tip</th>
              <th>{isAbsolute ? (scaleFactor === 1 ? "Kolicina" : `Kolicina ${formatScaleFactor(scaleFactor)}`) : "Pekarski %"}</th>
              <th>Napomena</th>
            </tr>
          </thead>
          <tbody>
            {groupedViewLines(editingIngredients).map((group) => (
              <React.Fragment key={group.title ?? `g-${group.lines[0]?.idx}`}>
                {group.title ? (
                  <tr className="recipe-group-header">
                    <td colSpan={5} style={{ fontWeight: 700, background: "#f0f0f0" }}>
                      {group.title}
                    </td>
                  </tr>
                ) : null}
                {group.lines.map(({ ing, idx }) => (
              <React.Fragment key={ing.id || idx}>
                <tr className={ing.line_type === "semi_finished" ? "recipe-line-semi-finished" : ""}>
                  <td>{idx + 1}</td>
                  <td>
                    {ing.line_type === "semi_finished" && (
                      <button
                        type="button"
                        className="btn-sm btn-secondary recipe-expand-btn"
                        onClick={() =>
                          setExpandedRows((prev) => ({ ...prev, [idx]: !prev[idx] }))
                        }
                        style={{ marginRight: 8 }}
                      >
                        {expandedRows[idx] ? "▼" : "▶"}
                      </button>
                    )}
                    {ing.ingredient_name}
                  </td>
                  <td>{ing.ingredient_type || "-"}</td>
                  <td className="percentage-value">
                    {isAbsolute
                      ? `${formatQuantity((Number(ing.quantity) || 0) * scaleFactor)} ${
                          unitTypes.find((u) => u.ID === ing.unit_type_id)?.Code || ""
                        }`.trim()
                      : ing.baker_percentage
                        ? `${ing.baker_percentage}%`
                        : ing.notes.match(/(\d+(?:[.,]\d+)?)\s*g\/kom/i)?.[0] || "—"}
                  </td>
                  <td>{ing.notes || "—"}</td>
                </tr>
                {ing.line_type === "semi_finished" && expandedRows[idx] && ing.child_recipe && (
                  <tr className="recipe-nested-row">
                    <td colSpan={5}>
                      <table className="mes-table mes-table-nested">
                        <thead>
                          <tr>
                            <th>Sirovina</th>
                            <th>Tip</th>
                            {/* Poluproizvod ima vlastitu osnovu: slasticarski biskvit
                                moze visjeti u pekarskoj recepturi i obratno. */}
                            <th>{isAbsoluteRecipe(ing.child_recipe) ? "Kolicina" : "PEK %"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(ing.child_recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe || []).map((sub, sidx) => (
                            <tr key={sidx}>
                              <td>{sub.Ingredient_IngredientToRecipeIngredient?.IngredientName}</td>
                              <td>{sub.Ingredient_IngredientToRecipeIngredient?.IngredientTypeName || "-"}</td>
                              <td>
                                {isAbsoluteRecipe(ing.child_recipe)
                                  ? `${formatQuantity(Number(sub.Quantity) || 0)} ${
                                      unitTypes.find((u) => u.ID === sub.UnitType)?.Code || ""
                                    }`.trim()
                                  : `${sub.BakerPercentage ?? 0}%`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <div className="empty-state" style={{ padding: "1rem" }}>
          <p>Recept nema definiranih sastojaka.</p>
        </div>
      )}

      {!detailLoading && renderParametersView()}
    </>
  );

  /**
   * Mixer setpoints, read-only. The full list is always shown so it is obvious what
   * still has to be entered; a parameter nobody has set stays blank rather than
   * borrowing a number from another product.
   */
  const renderParametersView = () => (
    <>
      <h4 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>Recepturni parametri</h4>
      {!hasSavedParameters && (
        <p className="modal-hint" style={{ marginTop: 0 }}>
          Recept jos nema unesene parametre.
        </p>
      )}
      <div className="mes-table-wrap">
        <table className="mes-table mes-table-compact">
          <thead>
            <tr>
              <th>Parametar</th>
              <th style={{ width: "120px" }}>Vrijednost</th>
              <th style={{ width: "90px" }}>Jedinica</th>
            </tr>
          </thead>
          <tbody>
            {RECIPE_PARAMETERS.map((def) => {
              const value = parameters[def.key];
              const isSet = value !== null && value !== undefined;
              return (
                <tr key={def.key}>
                  <td>{def.labelHr}</td>
                  <td className={isSet ? "percentage-value" : ""} style={isSet ? undefined : { opacity: 0.5 }}>
                    {isSet ? value : "—"}
                  </td>
                  <td>{def.unit || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderParametersEditMode = () => (
    <>
      <div className="recipe-details-grid">
        <div className="detail-item">
          <span className="detail-label">Proizvod</span>
          <span className="detail-value">{getProductName(selectedRecipe!.Product ?? 0)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Verzija</span>
          <span className="detail-value">v{getRecipeVersion(selectedRecipe!)}</span>
        </div>
      </div>

      <h4 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>Recepturni parametri</h4>
      <div className="form-grid-2">
        {RECIPE_PARAMETERS.map((def) => (
          <div className="form-group" key={def.key}>
            <label>
              {def.labelHr}
              {def.unit ? ` [${def.unit}]` : ""}
            </label>
            <NumberInput
              step="any"
              // NaN is what an empty field reports back, which keeps "not set"
              // distinguishable from a deliberate 0.
              emptyValue={NaN}
              value={parameters[def.key]}
              onValueChange={(v) => handleUpdateParameter(def.key, v)}
              disabled={savingParameters}
              placeholder="—"
            />
          </div>
        ))}
      </div>

      <p className="modal-hint">
        Napomena: Parametri se spremaju na ovu verziju recepta i ne stvaraju novu verziju.
        Prazna polja ostaju neunesena. Nova verzija recepta preuzima zadnje spremljene parametre.
      </p>
    </>
  );

  /**
   * Stupci za količinu ovise o osnovi: pekarska receptura ima jedan postotak,
   * slastičarska količinu i jedinicu (grami, komadi, listovi želatine).
   */
  const quantityHeaderCells = isAbsolute ? (
    <>
      <th style={{ width: "110px" }}>Kolicina</th>
      <th style={{ width: "100px" }}>Jedinica</th>
    </>
  ) : (
    <th style={{ width: "120px" }}>Pekarski %</th>
  );

  const quantityEditCells = (ing: EditableIngredient, idx: number) =>
    isAbsolute ? (
      <>
        <td>
          <NumberInput
            step="0.001"
            value={ing.quantity}
            onValueChange={(v) => handleUpdateIngredient(idx, "quantity", v)}
            style={{ width: "100%" }}
          />
        </td>
        <td>
          <select
            value={ing.unit_type_id ?? ""}
            onChange={(e) =>
              handleUpdateIngredient(idx, "unit_type_id", e.target.value ? Number(e.target.value) : undefined)
            }
            style={{ width: "100%" }}
          >
            <option value="">—</option>
            {unitTypes.map((u) => (
              <option key={u.ID} value={u.ID}>{u.Code}</option>
            ))}
          </select>
        </td>
      </>
    ) : (
      <td>
        <NumberInput
          step="0.1"
          value={ing.baker_percentage}
          onValueChange={(v) => handleUpdateIngredient(idx, "baker_percentage", v)}
          style={{ width: "100%" }}
        />
      </td>
    );

  const renderEditMode = () => {
    return (
      <>
        <div className="form-grid-2" style={{ marginBottom: "1.25rem" }}>
          <div className="form-group">
            <label>Pogon</label>
            <select
              value={unitDraft ?? ""}
              onChange={(e) => setUnitDraft(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— nije odreden —</option>
              {productionUnits.map((u) => (
                <option key={u.ID} value={u.ID}>{u.Name}</option>
              ))}
            </select>
            <span className="modal-hint">
              {isAbsolute
                ? "Slasticarna: kolicine u gramima, komadima ili listovima."
                : "Pekara: pekarski postotak brasna (PEK %)."}
            </span>
          </div>
          {isAbsolute && (
            <div className="form-group">
              <label>Mnozitelji</label>
              <input
                type="text"
                value={scaleFactorsDraft}
                onChange={(e) => setScaleFactorsDraft(e.target.value)}
                placeholder="npr. 5, 7.5, 10"
              />
              <span className="modal-hint">Razine na koje se ova receptura skalira.</span>
            </div>
          )}
        </div>

        {renderRecipeAnalytics()}

      <h4 style={{ marginBottom: "0.5rem" }}>Sirovine</h4>
      <div className="mes-table-wrap">
        <table className="mes-table ingredient-edit-table">
        <thead>
          <tr>
            <th style={{ width: "50px" }}>#</th>
            <th>Sirovina</th>
            <th style={{ width: "120px" }}>Tip</th>
            {quantityHeaderCells}
            <th style={{ width: "80px" }}></th>
            <th style={{ width: "50px" }}></th>
          </tr>
        </thead>
        <tbody>
          {editingIngredients
            .map((ing, idx) => ({ ing, idx }))
            .filter(({ ing }) => ing.line_type === "ingredient")
            .map(({ ing, idx }) => (
              <tr key={ing.id || `raw-${idx}`}>
                <td>{idx + 1}</td>
                <td>
                  <select
                    value={ing.ingredient_id || ""}
                    onChange={(e) => handleUpdateIngredient(idx, "ingredient_id", Number(e.target.value))}
                    style={{ width: "100%" }}
                  >
                    <option value="">-- Odaberi --</option>
                    {ingredients.map((i) => (
                      <option key={getIngredientId(i)} value={getIngredientId(i)}>
                        {i.IngredientName}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{ing.ingredient_type || "-"}</td>
                {quantityEditCells(ing, idx)}
                <td>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleMoveIngredient(idx, "up")}
                      disabled={idx === 0}
                    >
                      ↑
                    </button>
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleMoveIngredient(idx, "down")}
                      disabled={idx === editingIngredients.length - 1}
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td>
                  <button className="btn-sm btn-danger" onClick={() => handleRemoveIngredient(idx)}>✕</button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      </div>
      <button className="btn-secondary" onClick={handleAddRawIngredient} style={{ marginTop: "0.5rem" }}>
        + Dodaj sirovinu
      </button>

      <h4 style={{ marginTop: "1.25rem", marginBottom: "0.5rem" }}>Poluproizvodi</h4>
      <div className="mes-table-wrap">
        <table className="mes-table ingredient-edit-table">
        <thead>
          <tr>
            <th style={{ width: "50px" }}>#</th>
            <th>Poluproizvod</th>
            <th style={{ width: "120px" }}>Tip</th>
            {quantityHeaderCells}
            <th style={{ width: "80px" }}></th>
            <th style={{ width: "50px" }}></th>
          </tr>
        </thead>
        <tbody>
          {editingIngredients
            .map((ing, idx) => ({ ing, idx }))
            .filter(({ ing }) => ing.line_type === "semi_finished")
            .map(({ ing, idx }) => (
              <tr key={ing.id || `sf-${idx}`} className="recipe-line-semi-finished">
                <td>{idx + 1}</td>
                <td>
                  <select
                    value={ing.child_recipe_id || ""}
                    onChange={(e) =>
                      handleUpdateIngredient(idx, "child_recipe_id", Number(e.target.value))
                    }
                    style={{ width: "100%" }}
                  >
                    <option value="">-- Odaberi --</option>
                    {semiFinished.map((sf) => {
                      const rid = getSemiFinishedRecipeId(sf);
                      if (!rid) return null;
                      return (
                        <option key={sf.ID} value={rid}>
                          {sf.Name}
                        </option>
                      );
                    })}
                  </select>
                </td>
                <td>{ing.semi_finished_type || ing.ingredient_type || "-"}</td>
                {quantityEditCells(ing, idx)}
                <td>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleMoveIngredient(idx, "up")}
                      disabled={idx === 0}
                    >
                      ↑
                    </button>
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleMoveIngredient(idx, "down")}
                      disabled={idx === editingIngredients.length - 1}
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td>
                  <button className="btn-sm btn-danger" onClick={() => handleRemoveIngredient(idx)}>✕</button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      </div>
      <button className="btn-secondary" onClick={handleAddSemiFinished} style={{ marginTop: "0.5rem" }}>
        + Dodaj poluproizvod
      </button>

      <p className="modal-hint">
        Napomena: Spremanje ce stvoriti novu verziju recepta. Stara verzija ostaje sacuvana.
      </p>
    </>
  );
};

  return (
    <div className="mes-page recipes-page">
      <Header title="MES - Recepti" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Filters */}
          <div className="mes-section">
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <div className="control-group" style={{ marginBottom: 0 }}>
                <label>Proizvod:</label>
                <select
                  className="select-classic"
                  value={filterProductId || ""}
                  onChange={(e) => setFilterProductId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Svi proizvodi</option>
                  {products.map((p) => (
                    <option key={getProductId(p)} value={getProductId(p)}>
                      {p.ProductName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="checkbox-group" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={showHistory}
                  onChange={(e) => setShowHistory(e.target.checked)}
                />
                <label>Prikazi starije verzije</label>
              </div>
            </div>
          </div>

          {/* Recipes List */}
          {loading ? (
            <div className="mes-section loading-state">Ucitavanje...</div>
          ) : recipes.length === 0 ? (
            <div className="mes-section empty-state">
              <p>Nema recepata za odabrani filter.</p>
            </div>
          ) : (
            <div className="mes-section">
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>Naziv recepta</th>
                    <th>Proizvod</th>
                    <th>Verzija</th>
                    <th>Status verzije</th>
                    <th>Broj sastojaka</th>
                    <th>Osnova</th>
                    <th>Ukupno %</th>
                    <th>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {productGroups.map((group) => (
                    <React.Fragment key={group.productId}>
                      <tr>
                        <td style={{ fontWeight: 500 }}>{getRecipeName(group.current)}</td>
                        <td>{getProductName(group.productId)}</td>
                        <td>
                          v{getRecipeVersion(group.current)}
                          {group.older.length > 0 && (
                            <span className="recipe-version-hint"> (+{group.older.length})</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-pill status-${group.current.IsActive ? "active" : "inactive"}`}>
                            <span className="status-dot"></span>
                            {group.current.IsActive ? "Aktivna" : "Arhivirana"}
                          </span>
                        </td>
                        <td>{group.current.RecipeIngredient_RecipeToRecipeIngredient_Recipe?.length || 0}</td>
                        <td>{renderBasisBadge(group.current)}</td>
                        <td>
                          {/* Zbroj postotaka na slasticarskoj recepturi nije nula nego
                              nepostojeci podatak — grami i listovi se ne zbrajaju u %. */}
                          {isAbsoluteRecipe(group.current)
                            ? "—"
                            : `${getTotalBakerPercentage(
                                group.current.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [],
                              ).toFixed(1)}%`}
                        </td>
                        <td>
                          <button
                            className="btn-sm btn-secondary"
                            onClick={() => handleOpenRecipe(group.current)}
                          >
                            Detalji
                          </button>
                        </td>
                      </tr>
                      {showHistory &&
                        group.older.map((recipe) => (
                          <tr key={getRecipeId(recipe)} className="recipe-history-row">
                            <td style={{ paddingLeft: "1.5rem" }}>{getRecipeName(recipe)}</td>
                            <td>{getProductName(recipe.Product ?? group.productId)}</td>
                            <td>v{getRecipeVersion(recipe)}</td>
                            <td>
                              <span className="status-pill status-inactive">
                                <span className="status-dot"></span>
                                Arhivirana
                              </span>
                            </td>
                            <td>{recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe?.length || 0}</td>
                            <td>{renderBasisBadge(recipe)}</td>
                            <td>
                              {isAbsoluteRecipe(recipe)
                                ? "—"
                                : `${getTotalBakerPercentage(
                                    recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [],
                                  ).toFixed(1)}%`}
                            </td>
                            <td>
                              <button
                                className="btn-sm btn-secondary"
                                onClick={() => handleOpenRecipe(recipe)}
                              >
                                Detalji
                              </button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Recipe Details Modal */}
          {selectedRecipe && (
            <div className="mes-modal-overlay" onClick={handleCloseModal}>
              <div className="mes-modal mes-modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div className="modal-title-group">
                    <div className="modal-icon">📋</div>
                    <div className="modal-title-text">
                      <h3>{getRecipeName(selectedRecipe).toUpperCase()}</h3>
                      <p className="modal-subtitle">
                        {isEditing
                          ? "Uredivanje recepta"
                          : isEditingParameters
                          ? "Uredivanje recepturnih parametara"
                          : "Detalji recepta"}
                      </p>
                    </div>
                  </div>
                  <button className="close-btn" onClick={handleCloseModal}>✕</button>
                </div>
                <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {isEditing
                    ? renderEditMode()
                    : isEditingParameters
                    ? renderParametersEditMode()
                    : renderViewMode()}
                </div>
                <div className="modal-footer">
                  {isEditing ? (
                    <>
                      <button className="btn-cancel" onClick={handleCancelEdit} disabled={saving}>
                        Odustani
                      </button>
                      <button className="btn-submit" onClick={handleSaveRecipe} disabled={saving}>
                        {saving ? "Spremanje..." : "Spremi novu verziju"}
                      </button>
                    </>
                  ) : isEditingParameters ? (
                    <>
                      <button className="btn-cancel" onClick={handleCancelEditParameters} disabled={savingParameters}>
                        Odustani
                      </button>
                      <button className="btn-submit" onClick={handleSaveParameters} disabled={savingParameters}>
                        {savingParameters ? "Spremanje..." : "Spremi parametre"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn-cancel" onClick={handleCloseModal}>
                        Zatvori
                      </button>
                      {selectedRecipe.IsActive && (
                        <button
                          className="btn-secondary"
                          onClick={handleStartEditParameters}
                          disabled={detailLoading}
                        >
                          Uredi parametre
                        </button>
                      )}
                      {selectedRecipe.IsActive && (
                        // Disabled until the lines arrive: entering edit mode on an
                        // empty list and saving would persist a recipe with no
                        // ingredients.
                        <button className="btn-primary" onClick={handleStartEdit} disabled={detailLoading}>
                          {detailLoading ? "Ucitavanje..." : "Uredi recept"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default Recipes;
